import {
  ReportAvailability,
  ReportSectionId,
  ReportSectionRetryRequest,
} from '@digital-twins/shared-types/reporting'

import { isFailedExhibitSection } from '@/features/studies/report/reportViewState'
import type {
  ReportReadiness,
  TestWithResults,
} from '@/types/routes/test-results'

export type StudyReportExperiment = TestWithResults & {
  deckJsonKey?: string | null
  canonicalAnalysisJsonKey?: string | null
  /** Owner id, when the full-report route sends it. */
  userId?: string
  /** Owner organization token, when the full-report route sends it. */
  organizationToken?: string
}

export const EMPTY_REPORT_RESPONSES: any[] = []

export const defaultStudyReportReadiness: ReportReadiness = {
  isReady: false,
  overallStatus: 'processing',
  steps: {
    responses: { status: 'pending' },
    reportSummary: { status: 'pending' },
    deck: { status: 'pending' },
  },
}

export function hasStudyReportResponses(results: unknown): boolean {
  return (
    results !== null &&
    results !== undefined &&
    typeof results === 'object' &&
    Array.isArray((results as { responses?: unknown }).responses)
  )
}

export function getStudyReportName(
  study: StudyReportExperiment | null | undefined,
): string {
  if (!study) return 'Untitled Study'
  if (study.name) return study.name
  if (study.templateId) {
    return study.templateId
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return 'Untitled Study'
}

/**
 * The analyst flag a build without saved-audience membership carries
 * (`SAVED_AUDIENCE_UNAVAILABLE_FLAG_CODE`, python-api
 * `models/questionnaire/saved_audience.py`; a parity test pins both).
 */
export const SAVED_AUDIENCE_UNAVAILABLE_FLAG_CODE = 'saved_audience_unavailable'

/** Whether this analysis was built without its saved audience segments. */
export function savedAudienceUnavailable(
  analysis: { flags?: Array<{ code: string }> | null } | null | undefined,
): boolean {
  return (analysis?.flags ?? []).some(
    (flag) => flag.code === SAVED_AUDIENCE_UNAVAILABLE_FLAG_CODE,
  )
}

/** Whether the interpretation of readable results is still coming or will not come. */
export type InterpretationNotice = 'pending' | 'failed'

/**
 * The notice a report with readable facts but no readable Summary shows:
 * `pending` while its generation still runs, `failed` once it
 * ended, even when the Summary was left pending by a generation that is over,
 * so the reader gets a Retry. Null when there is no accepted
 * publication, no facts, or the Summary is ready or omitted.
 */
export function interpretationNotice(
  availability: ReportAvailability | null | undefined,
): InterpretationNotice | null {
  if (!availability || !availability.factsAvailable || availability.interpretationReady) {
    return null
  }
  const summary = availability.sections.find(
    (section) =>
      section.sectionId.kind === 'section' && section.sectionId.section === 'summary',
  )
  // An omitted Summary (deliberately removed, or not applicable) is not unfinished.
  if (!summary || summary.availability === 'omitted') {
    return null
  }
  const running =
    availability.generationLifecycle === 'running' ||
    availability.generationLifecycle === 'allocated' ||
    availability.reportStatus === 'generating'
  return running ? 'pending' : 'failed'
}

/**
 * Where the accepted publication's unfinished work sits: nowhere, only in
 * exhibits (single deck slides, told on the slide itself), or in a report
 * section or the deck as a whole (told by the page banner).
 */
export type ReportFailureScope = 'none' | 'exhibits' | 'sections'

/**
 * `exhibits` only when every report section is available or omitted, the
 * deck did not fail, and every unfinished exhibit is one the pager shows as
 * a failed slide (`isFailedExhibitSection`). Anything else unfinished,
 * including a pending exhibit, which has no placeholder, is `sections`.
 */
export function reportFailureScope(
  availability: ReportAvailability | null | undefined,
): ReportFailureScope {
  if (!availability) {
    return 'none'
  }
  if (availability.deckStatus === 'failed') {
    return 'sections'
  }
  let hasFailedExhibit = false
  for (const section of availability.sections) {
    if (isFailedExhibitSection(section)) {
      hasFailedExhibit = true
      continue
    }
    const settled =
      section.sectionId.kind === 'section'
        ? section.availability === 'available' || section.availability === 'omitted'
        : section.availability !== 'pending'
    if (!settled) {
      return 'sections'
    }
  }
  return hasFailedExhibit ? 'exhibits' : 'none'
}

/**
 * Whether readers must keep refreshing because the accepted publication is
 * still building its interpretation: `isReady` is already
 * true once facts are readable, so readiness alone would stop polling while
 * the Summary is still coming.
 */
export function interpretationStillBuilding(
  availability: ReportAvailability | null | undefined,
): boolean {
  return (
    interpretationNotice(availability) === 'pending' ||
    availability?.reportStatus === 'generating'
  )
}

/**
 * The section retry a Retry click sends: the accepted
 * generation, the revision the reader sees and the unfinished sections a
 * writer-only retry can finish (a failed or pending Summary, a failed or
 * facts-only exhibit). Null when the command should regenerate the whole
 * report instead: nothing named, nothing unfinished, or the facts or coding
 * themselves did not finish.
 */
export function sectionRetryRequest(
  availability: ReportAvailability | null | undefined,
): ReportSectionRetryRequest | null {
  if (!availability?.reportGenerationId) {
    return null
  }
  const sections: Array<ReportSectionId> = []
  for (const section of availability.sections) {
    const id = section.sectionId
    const unfinished =
      section.availability === 'failed' || section.availability === 'pending'
    if (id.kind === 'section') {
      if (id.section === 'summary' && unfinished) {
        sections.push(id)
      } else if (unfinished) {
        return null
      }
    } else if (unfinished || section.availability === 'facts_only') {
      sections.push(id)
    }
  }
  if (sections.length === 0) {
    return null
  }
  return {
    reportingContractVersion: 1,
    reportGenerationId: availability.reportGenerationId,
    basedOnPublicationRevision: availability.publicationRevision,
    sections,
  }
}

/** Retry refusals only a full regeneration can resolve. */
const FULL_REGENERATION_CODES: ReadonlySet<string> = new Set([
  'user_edited',
  'inputs_changed',
  'full_regeneration_required',
])

/** What a failed or refused regeneration request said. */
export interface RegenerationErrorDescription {
  /** The typed refusal code (`retry_failed`, `user_edited`, ...), or null. */
  code: string | null
  /** The HTTP status of the refusal, or null (a network failure). */
  status: number | null
  /** Whether the reader should be offered a full regeneration instead. */
  offersFullRegeneration: boolean
}

/**
 * The typed code and status of a regeneration error response, if any. The
 * server's message is never kept: it is English and may name internal codes.
 */
export function describeRegenerationError(error: unknown): RegenerationErrorDescription {
  const response = (error as { response?: { status?: unknown; data?: unknown } } | null)?.response
  const data = response?.data as { code?: unknown } | undefined
  const code = typeof data?.code === 'string' ? data.code : null
  const status = typeof response?.status === 'number' ? response.status : null
  return {
    code,
    status,
    offersFullRegeneration: code !== null && FULL_REGENERATION_CODES.has(code),
  }
}

/** The i18n keys (namespace `app`) a failed or refused regeneration is told with. */
export type RegenerationErrorMessageKey =
  | 'studyReport.retryFailed'
  | 'studyReport.retryNeedsFullRegeneration'
  | 'studyReport.retryErrors.reportChanged'
  | 'studyReport.retryErrors.inFlight'
  | 'studyReport.retryErrors.reportReplaced'
  | 'studyReport.retryErrors.alreadyFinished'
  | 'studyReport.retryErrors.studyStillRunning'
  | 'studyReport.retryErrors.studyNotFinished'

/** Typed refusals of a section retry, by the i18n key (namespace `app`) that tells them. */
const REFUSAL_MESSAGE_KEYS: Readonly<Record<string, RegenerationErrorMessageKey>> = {
  retry_failed: 'studyReport.retryFailed',
  stale_revision: 'studyReport.retryErrors.reportChanged',
  generation_in_flight: 'studyReport.retryErrors.inFlight',
  not_published_generation: 'studyReport.retryErrors.reportReplaced',
  section_not_unfinished: 'studyReport.retryErrors.alreadyFinished',
  // A section retry that ran but was not published names its disposition.
  rejected_stale: 'studyReport.retryErrors.reportChanged',
  rejected_conflict: 'studyReport.retryErrors.reportChanged',
  previous_retained: 'studyReport.retryErrors.reportChanged',
  checkpoint_held: 'studyReport.retryErrors.reportChanged',
  duplicate: 'studyReport.retryErrors.reportChanged',
  not_recorded: 'studyReport.retryErrors.reportChanged',
}

/**
 * The i18n key (namespace `app`) that tells a failed or refused regeneration
 * in the reader's language. Unknown failures read the generic
 * retry failure; the server's own message is never shown.
 */
export function regenerationErrorMessageKey(
  described: RegenerationErrorDescription,
  options: { isSectionRetry: boolean },
): RegenerationErrorMessageKey {
  const { code, status, offersFullRegeneration } = described
  if (offersFullRegeneration) {
    return 'studyReport.retryNeedsFullRegeneration'
  }
  if (code !== null && REFUSAL_MESSAGE_KEYS[code]) {
    return REFUSAL_MESSAGE_KEYS[code]
  }
  // Untyped refusals of a full regeneration: the study's run is still going
  // (409), or it has not finished (400).
  if (code === null && !options.isSectionRetry && status === 409) {
    return 'studyReport.retryErrors.studyStillRunning'
  }
  if (code === null && !options.isSectionRetry && status === 400) {
    return 'studyReport.retryErrors.studyNotFinished'
  }
  return 'studyReport.retryFailed'
}
